import React, { useRef, useEffect } from 'react';

interface LordIconProps {
  src: string;
  trigger?: 'hover' | 'click' | 'loop' | 'loop-on-hover' | 'in' | 'morph' | 'boomerang' | 'sequence';
  size?: number;
  /**
   * Default icon color (hex). When inside a button/link, pass both `color`
   * and `hoverColor` so the icon reacts to the parent hover state.
   */
  color?: string;
  /** Color to use when the parent button/link is hovered. */
  hoverColor?: string;
  color2?: string;
  state?: string;
  /**
   * CSS selector of the ancestor element that owns the hover/click trigger.
   * e.g. "button", "a", ".my-card"
   * The icon animation fires AND the color changes on hover of that element.
   */
  target?: string;
  className?: string;
}

function colorsString(primary: string, secondary?: string) {
  return secondary ? `primary:${primary},secondary:${secondary}` : `primary:${primary}`;
}

/**
 * Imperative lord-icon host - avoids React re-creating the custom element when
 * colors change (which caused a static ghost icon under the animated one).
 */
const LordIcon: React.FC<LordIconProps> = ({
  src,
  trigger = 'hover',
  size = 20,
  color = '#0065F4',
  hoverColor,
  color2,
  state,
  target,
  className = '',
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const iconRef = useRef<HTMLElement | null>(null);
  const colorRef = useRef({ color, hoverColor, color2 });
  colorRef.current = { color, hoverColor, color2 };

  // Mount / remount only when structural props change
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.replaceChildren();

    const el = document.createElement('lord-icon');
    el.setAttribute('src', src);
    el.setAttribute('trigger', trigger);
    if (state) el.setAttribute('state', state);
    if (target) el.setAttribute('target', target);
    el.setAttribute('colors', colorsString(colorRef.current.color, colorRef.current.color2));
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.display = 'block';
    el.style.overflow = 'hidden';

    wrapper.appendChild(el);
    iconRef.current = el;

    return () => {
      wrapper.replaceChildren();
      iconRef.current = null;
    };
    // color/hoverColor intentionally omitted - updated imperatively below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, trigger, state, target, size]);

  // Keep default color in sync when props change (without remount)
  useEffect(() => {
    const el = iconRef.current;
    if (!el) return;
    el.setAttribute('colors', colorsString(color, color2));
  }, [color, color2]);

  // Parent hover → color swap via setAttribute (no React re-render of lord-icon)
  useEffect(() => {
    if (!target || !wrapperRef.current) return;

    const ancestor = wrapperRef.current.closest<HTMLElement>(target);
    if (!ancestor) return;

    const apply = (primary: string) => {
      const el = iconRef.current;
      if (!el) return;
      el.setAttribute('colors', colorsString(primary, colorRef.current.color2));
    };

    const onEnter = () => {
      if (colorRef.current.hoverColor) apply(colorRef.current.hoverColor);
    };
    const onLeave = () => apply(colorRef.current.color);

    ancestor.addEventListener('mouseenter', onEnter);
    ancestor.addEventListener('mouseleave', onLeave);
    return () => {
      ancestor.removeEventListener('mouseenter', onEnter);
      ancestor.removeEventListener('mouseleave', onLeave);
    };
  }, [target, src, trigger, state, size]);

  return (
    <span
      ref={wrapperRef}
      className={`lord-icon-host ${className}`.trim()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
      aria-hidden
    />
  );
};

export default LordIcon;
